<!-- @format -->
<script lang="ts">
  import { getStoresContext } from "~/stores";
  import type { StoresKey } from "~/stores";
  import FilterByFloor from "../stores/FilterByFloor.svelte";
  import FilterByStore from "../stores/FilterByStore.svelte";
  import type { ManageAPI } from "~/manage-api/types";
  import { serviceLocationCodes } from "./serviceLocationCodes";

  export let request_store_key: StoresKey;
  const { stores, store, scdata, T, locale, service_locations } = getStoresContext(request_store_key);

  type Service = {
    id: number,
    code: string,
    name: string,
  };

  let current_floor: ManageAPI.FloorBase | undefined;
  let current_floor_id: number = 0;
  let current_floor_index: number = 0;
  let currentFloorStores: ManageAPI.Store[];
  let selected_store: ManageAPI.Store | undefined;
  let selected_store_id: number | undefined;
  let requiredServices: Service[];
  let serviceClicked: Service | undefined;
  let selectedServiceLocations: ManageAPI.ServiceLocation[] | undefined[];
  let isStoreOwnPage: boolean = false;

  $: allFloors = $scdata?.floors || [];
  $: current_floor = current_floor_id ? allFloors.find(x => x.id === current_floor_id) : allFloors[0];
  let naturalHeight: number;
  let naturalWidth: number;

  $: {
    if ($stores) {
      currentFloorStores = $stores.filter(x => x.floor?.id === current_floor?.id);
    }
  }

  $: {
    if ($service_locations) {
      const uniqueCodes = $service_locations.map(service => service.serviceType).filter((value, index, self) => self.indexOf(value) === index);
      requiredServices = uniqueCodes.map(code =>  serviceLocationCodes.find(service => service.id === code));
    }
  }

  // For stores's own page:
  $: {
    if ($store) {
      isStoreOwnPage = true;
      current_floor_id = $store.floor.id;
      current_floor_index = $store.floor.order;
      selected_store = $stores.find(x => x.id === $store.id);
    }
  }

  function changeFloor(event: CustomEvent) {
    selected_store = undefined;
    serviceClicked = undefined;
    selectedServiceLocations = [];
    return current_floor_id = event.detail;
  }
  function changeFloorIndex(event: CustomEvent) {
    return current_floor_index = event.detail;
  }

  function changeStore(event: CustomEvent) {
    selected_store = currentFloorStores.find(x => x.id === event.detail);
    return selected_store_id = selected_store?.id;
  }

  function calculateCoordinates(location: ManageAPI.ServiceLocation, naturalHeight: number, naturalWidth: number) {
    const left = (location.x / naturalWidth) * 100 + '%';
    const top = (location.y / naturalHeight) * 100 + '%';
    return `left: ${left}; top: ${top};`;
  }

  function onServiceClicked(service: Service) {
    serviceClicked = service;
    return selectedServiceLocations = $service_locations.filter(x => x.serviceType === service.id && x.floorIndex === current_floor_index);
  }

  $: t = $T;
</script>

{#if $locale}
  <section 
    class:container-fluid={!isStoreOwnPage}
    class:container-fluid-max-xl={!isStoreOwnPage}
  >
    {#if !isStoreOwnPage}
      <div class="row filter-section">
        <div class="col-lg-6 col-md-6 col-12">
          <FilterByFloor
            stores={$stores}
            {request_store_key}
            showAllOption={false}
            on:changeFloor={changeFloor} 
            on:changeFloorIndex={changeFloorIndex} 
          />
        </div>
        
        <div class="col-lg-6 col-md-6 col-12">
          <FilterByStore
            {request_store_key}
            {currentFloorStores}
            {selected_store}
            on:changeStore={changeStore}
          />
        </div>
      </div>
    {/if}

    {#if isStoreOwnPage}
      <h2 class="mb-4 storeSectionTitle">{t`floor map`}</h2>
    {/if}

    <div class="map-container-wrapper row justify-content-center">
      {#if !isStoreOwnPage}
        <div class="service-list-container col-lg-3 col-md-3 col-12">
          <p class="show-on-map-title">{t`service-locations.show-on-map`}</p>
          {#each requiredServices as service}
            <div 
              class="row service-item-row"
              on:click={() => onServiceClicked(service)}
              on:keydown={() => onServiceClicked(service)}
            >
              <img 
                src="/theme/citycon2023/images/pin_{service.code}.png" 
                alt="{service.name} icon" 
                class="service-item-img"
              >
              <p class="service-item">{t`service-locations.${service.code}`}</p>      
            </div>
          {/each}
        </div>
      {/if}
      
      <div 
        class="map-container col-12"
        class:col-lg-9={!isStoreOwnPage}
        class:col-md-9={!isStoreOwnPage}
      >
        <h2>{current_floor?.name}</h2>
        <img 
          src={current_floor?.floorMapURL} 
          alt={current_floor?.description}
          class="floor-map"
          bind:naturalHeight
          bind:naturalWidth
        >
        {#if selected_store && selected_store.storeLocations}
          <div class="col map-pin" style={calculateCoordinates(selected_store.storeLocations[0], naturalHeight, naturalWidth)}>
            {#if !isStoreOwnPage}<p>{selected_store.name}</p>{/if}
            <img 
              src="/theme/citycon2023/images/map-pin.png" 
              alt="map pin" 
              width="50"
            >
          </div>
        {/if}
        {#if serviceClicked}
          {#each selectedServiceLocations as location}
            <img 
              src="/theme/citycon2023/images/pin_{serviceClicked.code}.png"
              alt="{serviceClicked.name} icon"
              width="50"
              class="map-pin map-pin-services"
              style={calculateCoordinates(location, naturalHeight, naturalWidth)}
            >
          {/each}
        {/if}
      </div>
    </div>
  </section>
{/if}

<br />

<style lang="scss">
  .floor-map {
    position: relative;
    height: 100%;
    width: 100%;
  }

  .map-container {
    position: relative;
    height: 100%;
    order: 2;
    @media screen and (max-width: 768px) {
      order: 1;
    }
    h2 {
      position: absolute;
      z-index: 1;
      background-color: #333F48;
      color: #fff;
      height: 70px;
      width: 250px;
      text-align: center;
      line-height: 70px;
      @media screen and (max-width: 767px) {
        height: 30px;
        width: auto;
        line-height: 30px;
        font-size: 16px;
        padding: 0 10px;
      }
      @media screen and (min-width: 768px) and (max-width: 991px) {
        height: 40px;
        width: auto;
        line-height: 40px;
        font-size: 18px;
        padding: 0 20px;
      }
    }
  }

  .map-pin {
    position: absolute;
    transform: translate(-35px, -50px);
    width: auto;
    p {
      background-color: #333F48;
      color: #fff;
      text-align: center;
      font-size: 12px;
      height: 40px;
      margin-top: -55px;
      padding: 8px;
      min-width: 150px;
      transform: translate(calc(-50% + 25px), 0%);
      @media screen and (min-width: 768px) {
        font-size: 20px;
        height: 50px;
        padding: 10px 20px;
      }
    }
  }
  .map-pin-services {
    transform: translate(-40%, -100%);
    width: 25px;
    @media screen and (min-width: 768px) {
      width: 35px;
    }
    @media screen and (min-width: 1200px) {
      width: 50px;
    }
  }

  .service-list-container {
    margin-top: 10px;
    background-color: #333F48;
    color: #fff;
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 30px 20px;
    order: 1;
    @media screen and (max-width: 768px) {
      margin-bottom: 20px;
      order: 2;
    }

    .show-on-map-title {
      font-size: 20px;
      font-weight: bold;
      margin-bottom: 50px;
      @media screen and (min-width: 768px) {
        font-size: 22px;
      }
      @media screen and (min-width: 1200px) {
        font-size: 30px;
      }
    }

    .service-item-row {
      width: 100%;
      margin-bottom: 20px;
      .service-item-img {
        width: 40px;
        height: 40px;
        @media screen and (min-width: 768px) {
          width: 30px;
          height: 30px;
        }
        @media screen and (min-width: 1200px) {
          width: 50px;
          height: 50px;
        }
      }
      .service-item {
        font-size: 16px;
        font-weight: 600;
        line-height: 30px;
        margin-left: 20px;
        @media screen and (min-width: 768px) {
          font-size: 14px;
          line-height: 40px;
        }
        @media screen and (min-width: 1200px) {
          font-size: 22px;
          line-height: 50px;
        }
      }
    }
    .service-item-row:hover {
      cursor: pointer;
    }
  }
</style>
